import { loadDefaultJapaneseParser } from 'budoux';
import type { SubtitleStyle } from './types';

// budoux パーサー（日本語文節解析用）
const jaParser = loadDefaultJapaneseParser();

// テロップの最大行数。折り返しの既定値と、超過警告の文言がともにこの値を参照する
export const SUBTITLE_MAX_LINES = 2;

/**
 * テロップ用のCSSフォント指定文字列。
 * 計測と描画が同じフォールバック連鎖を使うために、ここで一元的に組み立てる。
 */
export function subtitleFontString(style: SubtitleStyle): string {
  return `${style.fontWeight} ${style.fontSize}px "${style.fontFamily}", "Noto Sans JP", sans-serif`;
}

/**
 * テキストの素の幅（文字間隔を含まない）をピクセルで返す計測器。
 * Canvas の measureText 等、環境の計測能力を呼び出し側が注入する。
 */
export type TextMeasurer = (text: string) => number;

export interface WrapResult {
  lines: string[];
  exceedsMaxLines: boolean;
  actualLineCount: number; // 切り捨て前の実際の行数
}

/**
 * 日本語テキストを自然な位置で折り返す。
 * budouxで文節境界を検出し、収まらない場合のみ文字単位で強制分割する。
 * maxLines を超えた分は切り捨て、超過は exceedsMaxLines で通知する。
 */
export function wrapJapaneseText(
  text: string,
  maxWidth: number,
  letterSpacing: number,
  measure: TextMeasurer,
  maxLines: number = SUBTITLE_MAX_LINES
): WrapResult {
  if (!text.trim()) {
    return { lines: [], exceedsMaxLines: false, actualLineCount: 0 };
  }

  // 文字間隔はコードポイント単位で数える（描画側の1文字ずつの描画と一致させる）
  const measureWidth = (str: string): number =>
    measure(str) + letterSpacing * ([...str].length - 1);

  if (measureWidth(text) <= maxWidth) {
    return { lines: [text], exceedsMaxLines: false, actualLineCount: 1 };
  }

  const phrases = jaParser.parse(text);
  const lines: string[] = [];
  let currentLine = '';

  for (const phrase of phrases) {
    const testLine = currentLine + phrase;
    if (measureWidth(testLine) <= maxWidth) {
      currentLine = testLine;
    } else {
      if (currentLine) {
        lines.push(currentLine);
        currentLine = phrase;
      } else {
        // 単一の文節が幅を超える場合は文字単位で強制分割
        const chars = [...phrase];
        let charLine = '';
        for (const char of chars) {
          const charTestLine = charLine + char;
          if (measureWidth(charTestLine) <= maxWidth) {
            charLine = charTestLine;
          } else {
            if (charLine) lines.push(charLine);
            charLine = char;
          }
        }
        currentLine = charLine;
      }
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  const actualLineCount = lines.length;
  const exceedsMaxLines = actualLineCount > maxLines;

  return {
    lines: exceedsMaxLines ? lines.slice(0, maxLines) : lines,
    exceedsMaxLines,
    actualLineCount,
  };
}

/**
 * 各行の中央Y座標を計算する（textBaseline='middle' 前提）。
 * 最下行の下端が「下から positionFromBottom」の位置に来るよう積み上げる。
 */
export function computeLineCenters(
  lineCount: number,
  fontSize: number,
  lineHeightRatio: number,
  positionFromBottom: number,
  canvasHeight: number
): number[] {
  const lineHeight = fontSize * lineHeightRatio;
  const bottomY = canvasHeight - positionFromBottom;
  const lastLineY = bottomY - fontSize / 2;
  const firstLineY = lastLineY - (lineCount - 1) * lineHeight;
  return Array.from({ length: lineCount }, (_, i) => firstLineY + lineHeight * i);
}

/**
 * シーンのスタイルをマージ（シーン個別スタイル > デフォルトスタイル）
 */
export function mergeSubtitleStyle(
  defaultStyle: SubtitleStyle,
  sceneStyle?: Partial<SubtitleStyle>
): SubtitleStyle {
  if (!sceneStyle) return defaultStyle;
  return { ...defaultStyle, ...sceneStyle };
}
